import { useEffect, useMemo } from 'react'
import type { JSX } from 'react'
import { useNavigate } from 'react-router-dom'
import { parseAsString, useQueryState } from 'nuqs'

import {
  EmptyState,
  SimpleSpinner,
  useLocalStorage,
} from '@buffer-mono/popcorn'

import {
  DEFAULT_VIEW_MODE,
  LAST_VIEW_MODE_LOCAL_STORAGE_KEY,
} from '~publish/components/Calendar/hooks/useCalendar'
import type { CalendarDateRangeViewMode } from '~publish/components/Calendar/hooks/useCalendarDateRangeViewMode'
import { filterOutUntaggedId } from '~publish/components/FilterByTag/FilterByTag'
import { useCurrentOrganization } from '~publish/contexts/AccountContext'
import { isCta } from '~publish/helpers/isCta'
import {
  preloadComposerDialog,
  usePostComposer,
} from '~publish/hooks/usePostComposer'
import { toQueryParamArray, useQueryParam } from '~publish/hooks/useQueryParam'
import { SEGMENT_NAMES } from '~publish/legacy/constants'
import { scheduleCalendar } from '~publish/legacy/routes'

/**
 * New Post Page
 * This page technically doesn't do anything
 * However it is used as a url which can be redirected/navigated to in order to automatically open the Composer
 * with the option to pass pre selected channels to open the Composer with.
 *
 * The main use case for this is the Global Plus button and keyboard shortcuts
 * This route/page only renders for a moment
 * and then the user is taken to the calendar page with the composer opened
 *
 * pre selecting channels with the composer can be done by passing channel[]=channelid
 * inside the query params. If no ids are provided, the composer preselects all available channels by default
 */
export const NewPostPage = (): JSX.Element => {
  const { isOpen, createNewPostInComposer } = usePostComposer()

  const channels = useCurrentOrganization()?.channels
  const channelsLength = channels?.length || 0
  // channelId/tags: this route is reachable from outside the app (deep
  // links, browser extension), so their array shape isn't ours to assume —
  // read them through the legacy hook's bracket-or-bare coercion instead of
  // nuqs's own array format.
  const [rawChannelIdsFromParams] = useQueryParam<string[]>('channelId')
  const channelIdsFromParams = useMemo(
    () => toQueryParamArray(rawChannelIdsFromParams),
    [rawChannelIdsFromParams],
  )
  const [rawSelectedTagIds] = useQueryParam<string[]>('tags')
  const selectedTagIds = useMemo(
    () => toQueryParamArray(rawSelectedTagIds),
    [rawSelectedTagIds],
  )
  const [textFromParams] = useQueryState('text', parseAsString)
  const [pictureFromParams] = useQueryState('picture', parseAsString)
  const [ctaFromParams] = useQueryState('cta', parseAsString)

  const navigate = useNavigate()
  const [calendarDateRangeViewMode] =
    useLocalStorage<CalendarDateRangeViewMode>(
      LAST_VIEW_MODE_LOCAL_STORAGE_KEY,
      DEFAULT_VIEW_MODE,
    )

  // This route always opens the composer, so preload its chunk in parallel
  // with the channel data load below instead of waiting on it.
  useEffect(() => {
    preloadComposerDialog()
  }, [])

  // Once the composer is open, redirect to the calendar so the user lands
  // there (instead of this blank loader) when they close it. Using `replace`
  // also clears /post/new from history — without this, the route would keep
  // re-triggering the composer on every render. See CORE-5620.
  useEffect(() => {
    if (isOpen) {
      navigate(
        scheduleCalendar.buildPathname({
          granularity: calendarDateRangeViewMode,
        }),
        { replace: true },
      )
    }
  }, [isOpen, navigate, calendarDateRangeViewMode])

  useEffect(() => {
    // Check that channels have loaded in case we need to open the composer with all channels
    if (!channels && !channelsLength) return
    // If there are channelIds from params, use them, otherwise use all channels if there are less than 3
    const selectedProfileIds = channelIdsFromParams.length
      ? channelIdsFromParams
      : channelsLength > 3
        ? []
        : 'all'
    const cta: CTA =
      typeof ctaFromParams === 'string' && isCta(ctaFromParams)
        ? ctaFromParams
        : SEGMENT_NAMES.CALENDAR_CREATE_A_POST

    void createNewPostInComposer({
      cta,
      channels: selectedProfileIds,
      prefillPostData: {
        // Filter out the special "untagged" filter value from URL params
        tags: selectedTagIds.length
          ? filterOutUntaggedId(selectedTagIds)
          : undefined,
        text: textFromParams ?? undefined,
        media: pictureFromParams
          ? {
              thumbnail: pictureFromParams,
              picture: pictureFromParams,
              // TODO: Review if we should use the actual width and height of the image
              // This is currently hardcoded because the types are mandatory and we don't have a way to get the actual width and height of the image
              width: '',
              height: '',
              progress: '100',
              uploaded: 'true',
            }
          : undefined,
      },
    })
  }, [
    channelIdsFromParams,
    createNewPostInComposer,
    selectedTagIds,
    textFromParams,
    pictureFromParams,
    channels,
    channelsLength,
    ctaFromParams,
  ])

  return (
    <EmptyState size="medium">
      <EmptyState.Icon>
        <SimpleSpinner size="medium" />
      </EmptyState.Icon>
      <EmptyState.Heading>Setting up your post...</EmptyState.Heading>
    </EmptyState>
  )
}
